import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { Bell, BellOff, Check, TrendingUp, Target, Flame, Clock } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';

interface NotificationPreferences {
  conversions: boolean;
  metrics: boolean;
  engagement: boolean;
  peakTimes: boolean;
}

export default function NotificationSettings() {
  useLanguage();
  const { permission, isSupported, requestPermission, sendNotification } = useNotifications();
  const { toast } = useToast();
  
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    conversions: true,
    metrics: true,
    engagement: true,
    peakTimes: true,
  });

  // Load preferences from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('notification-preferences');
    if (saved) {
      setPreferences(JSON.parse(saved));
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = (newPreferences: NotificationPreferences) => {
    setPreferences(newPreferences);
    localStorage.setItem('notification-preferences', JSON.stringify(newPreferences));
    toast({
      title: translateInterfaceText("Preferências salvas"),
      description: translateInterfaceText("Suas configurações de notificações foram atualizadas."),
    });
  };

  const handleToggle = (key: keyof NotificationPreferences) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key],
    };
    savePreferences(newPreferences);
  };

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    if (granted) {
      // Send a test notification
      sendNotification({
        title: translateInterfaceText("🎉 Notificações ativadas!"),
        body: translateInterfaceText("Você receberá alertas importantes sobre suas métricas e conversões."),
        tag: 'welcome',
      });
    }
  };

  const sendTestNotification = () => {
    if (permission !== 'granted') {
      toast({
        title: translateInterfaceText("Permissão necessária"),
        description: translateInterfaceText("Ative as notificações primeiro para enviar testes."),
        variant: "destructive",
      });
      return;
    }

    sendNotification({
      title: translateInterfaceText("🧪 Notificação de teste"),
      body: translateInterfaceText("Esta é uma notificação de teste do HostVSL. Tudo funcionando!"),
      tag: 'test',
    });
    
    toast({
      title: translateInterfaceText("Notificação enviada"),
      description: translateInterfaceText("Verifique se recebeu a notificação de teste."),
    });
  };

  const notificationTypes = [
    {
      key: 'conversions' as keyof NotificationPreferences,
      icon: TrendingUp,
      title: translateInterfaceText("Novas Conversões"),
      description: translateInterfaceText("Receba alertas quando houver novas conversões nos seus vídeos"),
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      example: translateInterfaceText("Exemplo: \"🎉 Nova conversão de R$ 297,00 no vídeo VSL Principal\""),
    },
    {
      key: 'metrics' as keyof NotificationPreferences,
      icon: Target,
      title: translateInterfaceText("Metas Alcançadas"),
      description: translateInterfaceText("Seja notificado quando suas métricas atingirem marcos importantes"),
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      example: translateInterfaceText("Exemplo: \"📊 Seu vídeo atingiu 1.000 visualizações!\""),
    },
    {
      key: 'engagement' as keyof NotificationPreferences,
      icon: Flame,
      title: translateInterfaceText("Alto Engajamento"),
      description: translateInterfaceText("Alertas quando seus vídeos tiverem picos de engajamento"),
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      example: translateInterfaceText("Exemplo: \"🔥 VSL Webinar está com 85% de engajamento\""),
    },
    {
      key: 'peakTimes' as keyof NotificationPreferences,
      icon: Clock,
      title: translateInterfaceText("Horários de Pico"),
      description: translateInterfaceText("Receba notificações sobre os melhores horários de visualização"),
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      example: translateInterfaceText("Exemplo: \"⏰ Horário de pico detectado (18:00 - 21:00)\""),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-[28px] font-bold text-[#111] mb-2">
            {translateInterfaceText("Configurações de Notificações")}</h1>
          <p className="text-[15px] text-[#666]">
            {translateInterfaceText("Gerencie os tipos de alertas que você deseja receber sobre suas métricas e conversões")}</p>
        </div>

        {/* Main Status Card */}
        <div 
          className="rounded-[16px] border border-[#E5E7EB] bg-white p-6 mb-6"
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                permission === 'granted' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {permission === 'granted' ? (
                  <Bell className="w-7 h-7 text-green-600" />
                ) : (
                  <BellOff className="w-7 h-7 text-gray-400" />
                )}
              </div>
              
              <div className="flex-1">
                <h2 className="text-[18px] font-semibold text-[#111] mb-2">
                  {permission === 'granted' ? translateInterfaceText("Notificações Ativas") :
                   permission === 'denied' ? translateInterfaceText("Notificações Bloqueadas") :
                   translateInterfaceText("Notificações Desativadas")}
                </h2>
                
                {!isSupported ? (
                  <p className="text-[14px] text-[#666]">
                    {translateInterfaceText("Seu navegador não suporta notificações push. Utilize Chrome, Firefox ou Safari para ativar este recurso.")}</p>
                ) : permission === 'granted' ? (
                  <p className="text-[14px] text-[#666]">
                    {translateInterfaceText("Você está recebendo notificações push sobre eventos importantes da sua conta. Configure abaixo quais tipos de alertas deseja receber.")}</p>
                ) : permission === 'denied' ? (
                  <p className="text-[14px] text-[#666]">
                    {translateInterfaceText("Você bloqueou as notificações. Para ativá-las novamente, acesse as configurações do seu navegador e permita notificações para este site.")}</p>
                ) : (
                  <p className="text-[14px] text-[#666]">
                    {translateInterfaceText("Ative as notificações para receber alertas em tempo real sobre conversões, métricas e atualizações importantes.")}</p>
                )}
              </div>
            </div>

            {permission === 'granted' && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-[13px] font-semibold text-green-700">{translateInterfaceText("Ativo")}</span>
              </div>
            )}
          </div>

          {permission === 'default' && isSupported && (
            <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
              <Button
                onClick={handleEnableNotifications}
                className="w-full h-[48px] bg-[#3B6FFF] hover:bg-[#2F55D4] text-white text-[15px] font-medium rounded-[10px]"
              >
                {translateInterfaceText("Ativar Notificações Push")}</Button>
            </div>
          )}

          {permission === 'granted' && (
            <div className="mt-6 pt-6 border-t border-[#E5E7EB] flex gap-3">
              <Button
                onClick={sendTestNotification}
                variant="outline"
                className="flex-1 h-[44px] border-[#E5E7EB] text-[#475569] text-[14px] font-medium rounded-[10px] hover:bg-gray-50"
              >
                {translateInterfaceText("Enviar Notificação Teste")}</Button>
            </div>
          )}
        </div>

        {/* Notification Types */}
        {permission === 'granted' && (
          <div className="space-y-4">
            <h3 className="text-[16px] font-semibold text-[#111] mb-4">
              {translateInterfaceText("Tipos de Notificações")}</h3>

            {notificationTypes.map((type) => (
              <div
                key={type.key}
                className="rounded-[14px] border border-[#E5E7EB] bg-white p-5"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-full ${type.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <type.icon className={`w-6 h-6 ${type.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[15px] font-semibold text-[#111]">
                        {type.title}
                      </h4>
                      <Switch
                        checked={preferences[type.key]}
                        onCheckedChange={() => handleToggle(type.key)}
                      />
                    </div>
                    
                    <p className="text-[13px] text-[#666] mb-3">
                      {type.description}
                    </p>

                    {preferences[type.key] && (
                      <div className="bg-[#F7F9FC] rounded-[8px] p-3 border border-[#E5E7EB]">
                        <p className="text-[12px] text-[#666] italic">
                          {type.example}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div 
          className="mt-6 rounded-[14px] border border-[#E5E7EB] bg-[#F7F9FC] p-5"
        >
          <h4 className="text-[14px] font-semibold text-[#111] mb-2">
            {translateInterfaceText("💡 Sobre as notificações")}</h4>
          <ul className="space-y-2 text-[13px] text-[#666]">
            <li>{translateInterfaceText("• As notificações funcionam mesmo com o navegador fechado (em segundo plano)")}</li>
            <li>{translateInterfaceText("• Você pode desativar notificações específicas sem perder as outras")}</li>
            <li>{translateInterfaceText("• Use o botão \"Enviar Notificação Teste\" para verificar se está tudo funcionando")}</li>
            <li>{translateInterfaceText("• As preferências são salvas automaticamente ao alterar qualquer configuração")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
